import React from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { AssessmentQuestion, AssessmentState } from '../models/assessment';
import { LikertAnswerResult } from './AssessmentTakerAnswerResult';

const ResultsSection = styled.section`
  margin-bottom: ${Grid._8};
`;

const ResultsSectionHeading = styled.h3`
  margin: 0 0 ${Grid._5} 0;
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
`;

const ResultsList = styled.div`
  background: ${Color.white};
  border: 1px solid ${Color.Gray._20};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.small};
  overflow: hidden;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Grid._4} ${Grid._5};

  &:not(:last-child) {
    border-bottom: 1px solid ${Color.Gray._10};
  }
`;

const CategoryName = styled.span`
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.medium};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.tall};
`;

const CategoryScore = styled.span`
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  white-space: nowrap;
  margin-left: ${Grid._4};
`;

const CategoryScoreMax = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
`;

const QuestionCard = styled.div`
  padding: ${Grid._5};
  border-bottom: 1px solid ${Color.Gray._20};

  &:last-child {
    border-bottom: none;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Grid._4};
  margin-bottom: ${Grid._5};
`;

const QuestionTitle = styled.h4`
  margin: 0;
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: ${Type.Leading.tall};
`;

const QuestionLabel = styled.span`
  font-size: ${Type.Scale._1};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  display: block;
  margin-bottom: ${Grid._1};
`;

type AssessmentTakerResultsProps = {
  assessment: AssessmentState;
};

export function AssessmentTakerResults({ assessment }: AssessmentTakerResultsProps) {
  const { questions, likertScales, scores } = assessment;
  const categoryResults = scores ? Object.entries(scores.subscaleScores) : [];

  const renderAnswerResult = (question: AssessmentQuestion) => {
    const { kind } = question;
    if (kind === 'Likert') {
      const scale = likertScales[question.likertScaleID];
      if (!scale) return null;
      return <LikertAnswerResult questionID={question.questionID} answer={question.answer} scale={scale} />;
    }

    // TODO: handle display of other question kinds
    else if (kind === 'SingleChoice') {
      return null;
    } else if (kind === 'FreeText') {
      return null;
    } else {
      // ensure TS throws error if all question kinds aren't covered
      const exhaustiveCheck: never = kind;
      return exhaustiveCheck;
    }
  };

  return (
    <>
      {categoryResults.length > 0 && (
        <ResultsSection>
          <ResultsSectionHeading>Your Category Summary</ResultsSectionHeading>
          <ResultsList>
            {categoryResults.map(([subscaleId, result]) => (
              <CategoryRow key={subscaleId}>
                <CategoryName>{result.name}</CategoryName>
                <CategoryScore>
                  {result.score.toFixed(2)}
                  <CategoryScoreMax> / 5</CategoryScoreMax>
                </CategoryScore>
              </CategoryRow>
            ))}
          </ResultsList>
        </ResultsSection>
      )}
      {questions && (
        <ResultsSection>
          <ResultsSectionHeading>Your Responses</ResultsSectionHeading>
          <ResultsList>
            {questions.map((question: AssessmentQuestion, i: number) => (
              <QuestionCard key={question.questionID}>
                <QuestionHeader>
                  <div>
                    <QuestionLabel>
                      Question {i + 1} of {questions.length}
                    </QuestionLabel>
                    <QuestionTitle>{question.text}</QuestionTitle>
                  </div>
                </QuestionHeader>
                {renderAnswerResult(question)}
              </QuestionCard>
            ))}
          </ResultsList>
        </ResultsSection>
      )}
    </>
  );
}
